import type {IErrorReporter} from '@/src/ports/services/monitoring'
import type {SessionProcessResult} from '@/src/ports/services/session-response-processor'

export class ApiError extends Error {
  messageNormalized: string = ''
  httpCode: number = 500

  constructor(message?: string, httpCode?: number) {
    super(message)
    this.httpCode = httpCode ?? 500
    this.splitErrorCode(message ?? '')
  }

  private splitErrorCode(message: string) {
    const match = message.match(/API:\[(\d+)\]:([\s\S]*)/)
    if (match) {
      this.messageNormalized = match[2] ?? message
      this.httpCode = parseInt(match[1] ?? '500')
    } else {
      this.messageNormalized = message
    }
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, 'headers'> {
  onResponse?: (response: Response) => Promise<void>
  onResponseData?: (responseData: ApiResponse) => Promise<void>
  data?: Record<any, any>
  params?: Record<any, any>
  headers?: {
    'Content-Type'?: string
    Cookie?: string
    'X-Auth'?: string
    'X-Mobile'?: string
    'x-csrf-token'?: string
    'x-mobile-token'?: string
    'X-Real-Ip'?: string
    'X-API-Key'?: string
    'X-Trace-Id'?: string
    'X-Span-Id'?: string
    'X-Mindbox-DeviceId'?: string
    'X-Runlab-Client'?: string
    'X-Client-Ip'?: string
    'X-Forwarded-For'?: string
  }
  /** Public request: no dynamic credentials/IP reads and no session response processing. */
  withoutSession?: boolean
  /** Captured before async request construction; never recaptured after a reset. */
  sessionGeneration?: number
  allowCookieMutation?: boolean
  isMobile?: boolean
  mobileCsrf?: string | null
  onSessionProcessed?: (result: SessionProcessResult) => void
}

export interface IApiClientService {
  requestBuilder: IApiRequestBuilder
  responseHandler: IApiResponseHandler

  get<ResponseType>(url: string, options?: ApiRequestOptions): Promise<ResponseType>
  post<ResponseType>(url: string, options?: ApiRequestOptions): Promise<ResponseType>
}

export interface IMayBeNeedProxyResolver {
  needProxy(path: string): boolean
}

export interface IEndpointResolver {
  resolve(path: string): string
}

export interface IApiRequestBuilder {
  endpointResolver: IEndpointResolver

  build(
    url: string,
    options?: ApiRequestOptions,
  ): Promise<{url: string; options: ApiRequestOptions}>
}

export interface IApiResponseHandler {
  errorReporter: IErrorReporter

  redirect(path: string): Promise<never>
  handle<ResponseType>(
    response: Response,
    options: ApiRequestOptions,
  ): Promise<ResponseType & Partial<ApiResponse>>
}

export type IApiClientServiceFactory = (target?: 'api' | 'web-api') => IApiClientService

export const IApiClientServiceFactorySymbol = Symbol.for('IApiClientServiceFactory')
